import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import VideoIcon from '@/components/icons/VideoIcon';
import UsersIcon from '@/components/icons/UsersIcon';
import GiftIcon from '@/components/icons/GiftIcon';
import ListIcon from '@/components/icons/ListIcon';
import QuestionIcon from '@/components/icons/QuestionIcon';
import TutorialIcon from '@/components/icons/TutorialIcon';
import { Hourglass, Info, Paperclip, Volume2, X } from 'lucide-react';
import { useChannelName } from '@/routes/AblyAuth';
import type { ModeratorToken, TriggerEventInput } from '@/features/live/types';
import { LiveKitRoom } from '@/components/pages/live/LiveKitRoom';
import LiveLaunchOverlay from '@/components/pages/live/LiveLaunchOverlay';
import LiveConfirmedToast from '@/components/pages/live/LiveConfirmedToast';
import { isLiveConfirmed, shouldShowLaunchOverlay } from '@/lib/liveReadiness';
import { useEvergreenVideoSource } from '@/hooks/useEvergreenVideoSource';
import { toast } from 'react-toastify';
import YouTubeHostStage from '@/components/pages/live/admin/YouTubeHostStage';
import type { VideoLayoutState } from '@/components/pages/live/admin/VideoLiveView';
import type { RootState } from '@/app/store';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchPlayback } from '@/features/live/liveThunks';
import {
  listOfferTemplates,
  listQuizTemplates,
  listSurveyTemplates,
  listWebinarResources,
  listWebinarSlides,
} from '@/features/webinar/webinarThunks';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import FooterBar from '@/components/pages/live/FootBar';
import type {
  WebinarResource,
  WebinarSession,
  OfferTemplate,
  SurveyTemplate,
} from '@/features/webinar/types';
import type { LiveSettings } from '@/components/pages/live/FootBar';
import VideosList from '@/components/pages/live/admin/VideosList';
import PresentersList from '@/components/pages/live/admin/PresentersList';
import SlidesList from '@/components/pages/live/admin/SlidesList';
import OfferList from '@/components/pages/live/admin/OfferList';
import SharedResourceLiveList from '@/components/pages/live/admin/SharedResourceLiveList';
import QuizsList from '@/components/pages/live/admin/QuizsList';
import SurveysList from '@/components/pages/live/admin/SurveysList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getConditionalBackgroundStyle, getConditionalIconColor } from './AdminLivePage';
import ChatView from '@/components/pages/live/ChatView';
import AttendeesListView from '@/components/pages/live/admin/AttendeesListView';
import LiveResourceBadge from '@/components/pages/live/LiveResourceBadge';
import { Button } from '@/components/ui/button';
import { DateTime } from 'luxon';
import { formatPlannedLiveDate } from '@/lib/formatPlannedDate';
import { useKeepMountedOnce } from '@/hooks/useKeepMountedOnce';

export default function ModeratorLivePage({
  moderatorToken,
  workspaceId,
  webinarId,
  sessionId,
  visualIdentity,
  handleTriggerEvent,
  onVideoLayoutChange,
  handleCloseSession,
  currentWebinarSession,
  liveSettings,
  onLiveSettingsChange,
  chatModeUpdating,
  liveParticipantCount,
  isYouTube,
  youtubeUrl,
}: {
  moderatorToken: ModeratorToken;
  workspaceId: number;
  webinarId: number;
  sessionId: number;
  visualIdentity: WorkspaceVisualIdentity;
  handleTriggerEvent: (event: TriggerEventInput) => void;
  onVideoLayoutChange: (state: VideoLayoutState) => void;
  handleCloseSession: () => void;
  currentWebinarSession: WebinarSession;
  liveSettings: LiveSettings;
  onLiveSettingsChange: (settings: LiveSettings) => void;
  chatModeUpdating?: boolean;
  liveParticipantCount: number;
  isYouTube?: boolean;
  youtubeUrl?: string | null;
}) {
  const dispatch = useAppDispatch();
  const evergreenVideoRef = useRef<HTMLVideoElement>(null);
  const user = useAppSelector((state: RootState) => state.auth.user);
  const channelName = useChannelName();
  const playbackInfo = useAppSelector((s: RootState) => s.live.playbackInfo);
  const streamStartStatus = useAppSelector((s: RootState) => s.live.streamStartStatus);
  const liveConfirmed = isLiveConfirmed(streamStartStatus);
  const sawStreamPendingRef = useRef(false);
  const prevLiveConfirmedRef = useRef(liveConfirmed);
  useEffect(() => {
    if (streamStartStatus === 'PENDING') sawStreamPendingRef.current = true;
    if (liveConfirmed && !prevLiveConfirmedRef.current && sawStreamPendingRef.current) {
      toast.success(<LiveConfirmedToast />);
    }
    prevLiveConfirmedRef.current = liveConfirmed;
  }, [liveConfirmed, streamStartStatus]);

  const { t, i18n } = useTranslation('webinars');

  const {
    webinarResources,
    webinarResourcesLoading,
    presenters,
    presentersLoading,
    speakers,
    webinarSlides,
    webinarSlidesLoading,
    offerTemplates,
    quizTemplates,
    surveyTemplates,
    surveyTemplatesLoading,
  } = useAppSelector((state: RootState) => state.webinar);
  const { adminResourceStates } = useAppSelector((state: RootState) => state.live);

  const isEvergreen = currentWebinarSession.sessionType === 'EVERGREEN';
  const showLaunchOverlay = shouldShowLaunchOverlay(isEvergreen, streamStartStatus);
  const sessionIdentifier = currentWebinarSession.identifier;

  const playbackMatchesSession = useMemo(() => {
    if (
      !playbackInfo ||
      playbackInfo.sessionIdentifier !== sessionIdentifier ||
      Number(playbackInfo.webinarId) !== webinarId ||
      playbackInfo.sessionId !== sessionId
    ) {
      return false;
    }
    return true;
  }, [playbackInfo, sessionIdentifier, webinarId, sessionId]);

  const hasModeratorPlaybackReady = useMemo(() => {
    if (!playbackMatchesSession || !playbackInfo) {
      return false;
    }
    const p = playbackInfo;
    return !!(
      p.mp4Url?.trim() ||
      p.hlsLiveUrl?.trim() ||
      p.desktop?.hlsLiveUrl?.trim() ||
      p.mobile?.hlsLiveUrl?.trim() ||
      p.tablet?.hlsLiveUrl?.trim()
    );
  }, [playbackMatchesSession, playbackInfo]);

  const [isEvergreenMuted, setIsEvergreenMuted] = useState(true);
  const [evergreenFooterSeconds, setEvergreenFooterSeconds] = useState(0);

  useEffect(() => {
    if (!isEvergreen || !workspaceId || !webinarId || !sessionId) {
      return;
    }
    if (hasModeratorPlaybackReady) {
      return;
    }
    const intervalMs = 2500;
    const id = setInterval(() => {
      void dispatch(fetchPlayback({ workspaceId, webinarId, sessionId }));
    }, intervalMs);
    return () => clearInterval(id);
  }, [isEvergreen, workspaceId, webinarId, sessionId, hasModeratorPlaybackReady, dispatch]);

  const getEvergreenElapsedSeconds = useCallback(() => {
    if (!isEvergreen) return 0;
    const startedAt = currentWebinarSession?.startedAt;
    if (!startedAt) return 0;
    const startUtc = DateTime.fromISO(startedAt, { zone: 'utc' });
    if (!startUtc.isValid) return 0;
    const nowUtc = DateTime.utc();
    return Math.max(0, Math.floor(nowUtc.diff(startUtc, 'seconds').seconds));
  }, [isEvergreen, currentWebinarSession?.startedAt]);

  // La page modérateur n'a pas de filet anti-avance : le déplacement est direct.
  const seekModeratorEvergreenTo = useCallback((video: HTMLVideoElement, target: number) => {
    if (!Number.isFinite(target)) return;
    video.currentTime = target;
  }, []);

  const { activeUrl: evergreenVideoUrl } = useEvergreenVideoSource({
    enabled: isEvergreen && playbackMatchesSession,
    videoRef: evergreenVideoRef,
    primaryUrl: playbackMatchesSession ? playbackInfo?.mp4Url : null,
    fallbackUrl: playbackMatchesSession ? playbackInfo?.fallbackVideoUrl : null,
    getResumePosition: getEvergreenElapsedSeconds,
    seekTo: seekModeratorEvergreenTo,
  });

  useEffect(() => {
    if (!isEvergreen) return;
    const video = evergreenVideoRef.current;
    if (!video) return;
    video.muted = isEvergreenMuted;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const tryPlay = () => {
      void video.play().catch(() => {
        video.muted = true;
        void video.play().catch(() => {});
      });
    };

    const seekAndPlay = () => {
      const elapsedSeconds = getEvergreenElapsedSeconds();
      const duration = video.duration;
      const target =
        Number.isFinite(duration) && duration > 0
          ? Math.min(elapsedSeconds, Math.max(0, duration - 0.25))
          : elapsedSeconds;
      video.currentTime = target;
      tryPlay();
    };

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      seekAndPlay();
    } else {
      video.addEventListener('loadedmetadata', seekAndPlay, { once: true });
    }
    video.addEventListener('canplay', tryPlay);

    retryTimer = setTimeout(() => {
      if (video.paused) {
        tryPlay();
      }
    }, 700);

    return () => {
      video.removeEventListener('canplay', tryPlay);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isEvergreen, getEvergreenElapsedSeconds, evergreenVideoUrl, isEvergreenMuted]);

  useEffect(() => {
    if (!isEvergreen) return;
    if (!currentWebinarSession?.startedAt) return;

    const resync = () => {
      if (document.visibilityState !== 'visible') return;
      const video = evergreenVideoRef.current;
      if (!video) return;
      video.muted = isEvergreenMuted;

      const elapsedSeconds = getEvergreenElapsedSeconds();
      const duration = video.duration;
      const target =
        Number.isFinite(duration) && duration > 0
          ? Math.min(elapsedSeconds, Math.max(0, duration - 0.25))
          : elapsedSeconds;
      video.currentTime = target;

      void video.play().catch(() => {
        video.muted = true;
        void video.play().catch(() => {});
      });
    };

    const onVisibilityChange = () => resync();
    const onFocus = () => resync();

    document.addEventListener('visibilitychange', onVisibilityChange);
    globalThis.addEventListener('focus', onFocus);
    resync();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      globalThis.removeEventListener('focus', onFocus);
    };
  }, [isEvergreen, currentWebinarSession?.startedAt, isEvergreenMuted, getEvergreenElapsedSeconds]);

  const handleActivateEvergreenSound = () => {
    const video = evergreenVideoRef.current;
    if (!video) return;
    video.muted = false;
    setIsEvergreenMuted(false);
  };

  useEffect(() => {
    if (!isEvergreen) return;
    const tick = () => {
      const v = evergreenVideoRef.current;
      if (v && Number.isFinite(v.currentTime)) {
        setEvergreenFooterSeconds(Math.floor(v.currentTime));
      } else if (currentWebinarSession?.startedAt) {
        const startUtc = DateTime.fromISO(currentWebinarSession.startedAt, { zone: 'utc' });
        if (startUtc.isValid) {
          const nowUtc = DateTime.utc();
          setEvergreenFooterSeconds(
            Math.max(0, Math.floor(nowUtc.diff(startUtc, 'seconds').seconds)),
          );
        }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isEvergreen, currentWebinarSession?.startedAt]);

  const offers: OfferTemplate[] =
    offerTemplates?.filter((offer) => offer.thankYouPageOffer !== true) || [];

  const quizs = quizTemplates?.filter((quiz) => quiz.waitingRoomQuiz !== true) || [];

  const surveys: SurveyTemplate[] =
    surveyTemplates?.filter((survey) => survey.waitingRoomSurvey !== true) || [];

  const [activePresenters, setActivePresenters] = useState(false);
  const [activeVideos, setActiveVideos] = useState(false);
  const [activeSlides, setActiveSlides] = useState(false);
  const [activeOffer, setActiveOffer] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState(false);
  const [activeResources, setActiveResources] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('chat');
  const [isUpgrade, _setIsUpgrade] = useState<boolean>(false);
  const formattedPlannedDate = useMemo(
    () => formatPlannedLiveDate(currentWebinarSession?.utcPlannedDate, i18n.language),
    [currentWebinarSession?.utcPlannedDate, i18n.language],
  );

  const theme = visualIdentity?.theme || 'LIGHT';
  const secondaryBackgroundColor = visualIdentity?.secondaryBackgroundColor || '#6DFFCD';
  const contrastColor = visualIdentity?.contrastColor || '#F3F3F4';

  const videos: WebinarResource[] =
    webinarResources?.filter(
      (resource) =>
        resource.mimeType === 'video/mp4' ||
        resource.mimeType === 'video/quicktime' ||
        resource.mimeType === 'video/webm' ||
        resource.mimeType === 'video/ogg' ||
        resource.mimeType === 'video/avi' ||
        resource.mimeType === 'video/mov' ||
        resource.mimeType === 'video/wmv' ||
        resource.mimeType === 'video/flv' ||
        resource.mimeType === 'video/mkv' ||
        resource.mimeType === 'video/mpg' ||
        resource.mimeType === 'video/mpeg' ||
        resource.mimeType === 'video/m4v' ||
        resource.mimeType === 'video/m4p',
    ) || [];

  const allMenuItems = [
    { id: 'presenters', title: t('adminLive.cards.presenters'), icon: UsersIcon },
    { id: 'videos', title: t('adminLive.cards.videos'), icon: VideoIcon },
    { id: 'slides', title: t('adminLive.cards.slides'), icon: TutorialIcon },
    { id: 'offer', title: t('adminLive.cards.offers'), icon: GiftIcon },
    { id: 'quiz', title: t('adminLive.cards.quiz'), icon: QuestionIcon },
    { id: 'resources', title: t('adminLive.cards.resources'), icon: Paperclip },
    { id: 'survey', title: t('adminLive.cards.surveys'), icon: ListIcon },
  ] as const;
  const menuItems = isYouTube
    ? allMenuItems.filter(
        (item) =>
          item.id === 'offer' ||
          item.id === 'quiz' ||
          item.id === 'resources' ||
          item.id === 'survey',
      )
    : allMenuItems;

  const getLiveCount = (id: string) => {
    switch (id) {
      case 'videos':
        return adminResourceStates.filter((s) => s.resourceType === 'WEBINAR_RESOURCE' && s.live)
          .length;
      case 'slides':
        return adminResourceStates.filter((s) => s.resourceType === 'WEBINAR_SLIDE' && s.live)
          .length;
      case 'offer':
        return adminResourceStates.filter((s) => s.resourceType === 'OFFER_TEMPLATE' && s.live)
          .length;
      case 'quiz':
        return adminResourceStates.filter((s) => s.resourceType === 'QUIZ_TEMPLATE' && s.live)
          .length;
      case 'survey':
        return adminResourceStates.filter((s) => s.resourceType === 'SURVEY_TEMPLATE' && s.live)
          .length;
      case 'resources':
        return adminResourceStates.filter((s) => s.resourceType === 'SHARED_RESOURCE' && s.live)
          .length;
      default:
        return 0;
    }
  };

  const isItemActive = (id: string) => {
    switch (id) {
      case 'presenters':
        return activePresenters;
      case 'videos':
        return activeVideos;
      case 'slides':
        return activeSlides;
      case 'offer':
        return activeOffer;
      case 'quiz':
        return activeQuiz;
      case 'survey':
        return activeSurvey;
      case 'resources':
        return activeResources;
      default:
        return false;
    }
  };

  const hasActiveItem =
    activePresenters ||
    activeVideos ||
    activeSlides ||
    activeOffer ||
    activeQuiz ||
    activeSurvey ||
    activeResources;

  /** Les miniatures rechargent tous les PDF à chaque montage : on ne démonte plus après la 1re ouverture. */
  const slidesMounted = useKeepMountedOnce(activeSlides);

  const handleCloseActiveItem = () => {
    setActivePresenters(false);
    setActiveVideos(false);
    setActiveSlides(false);
    setActiveOffer(false);
    setActiveQuiz(false);
    setActiveSurvey(false);
    setActiveResources(false);
  };

  const handleItemActivation = (
    itemType: 'presenters' | 'videos' | 'slides' | 'offer' | 'quiz' | 'survey' | 'resources',
  ) => {
    if (
      (itemType === 'presenters' && activePresenters) ||
      (itemType === 'videos' && activeVideos) ||
      (itemType === 'slides' && activeSlides) ||
      (itemType === 'offer' && activeOffer) ||
      (itemType === 'quiz' && activeQuiz) ||
      (itemType === 'survey' && activeSurvey) ||
      (itemType === 'resources' && activeResources)
    ) {
      setActivePresenters(false);
      setActiveVideos(false);
      setActiveSlides(false);
      setActiveOffer(false);
      setActiveQuiz(false);
      setActiveSurvey(false);
      setActiveResources(false);
      return;
    }

    setActivePresenters(false);
    setActiveVideos(false);
    setActiveSlides(false);
    setActiveOffer(false);
    setActiveQuiz(false);
    setActiveSurvey(false);
    setActiveResources(false);

    switch (itemType) {
      case 'presenters':
        setActivePresenters(true);
        break;
      // Les panneaux restent montés : sans ce rechargement à l'ouverture, une création faite
      // ailleurs pendant le live n'apparaîtrait qu'après un rafraîchissement de la page.
      case 'videos':
        setActiveVideos(true);
        void dispatch(listWebinarResources({ workspaceId, webinarId, page: 0, size: 30 }));
        break;
      case 'slides':
        setActiveSlides(true);
        void dispatch(listWebinarSlides({ workspaceId, webinarId }));
        break;
      case 'offer':
        setActiveOffer(true);
        void dispatch(listOfferTemplates({ workspaceId, webinarId }));
        break;
      case 'quiz':
        setActiveQuiz(true);
        void dispatch(listQuizTemplates({ workspaceId, webinarId }));
        break;
      case 'survey':
        setActiveSurvey(true);
        void dispatch(listSurveyTemplates({ workspaceId, webinarId }));
        break;
      case 'resources':
        setActiveResources(true);
        break;
    }
  };

  return (
    <div
      className="min-h-screen h-screen max-w-[2400px] mx-auto flex items-stretch justify-center gap-6"
      style={{
        backgroundColor: visualIdentity?.backgroundColor || '#FFFFFF',
      }}
    >
      {showLaunchOverlay && (
        <LiveLaunchOverlay variant={streamStartStatus === 'FAILED' ? 'failed' : undefined} />
      )}
      <div
        className={`${hasActiveItem ? 'w-1/3' : 'w-1/2'}  pl-6 py-6 h-full min-h-0 flex flex-col justify-between`}
      >
        {/* camera Section */}
        <div className="w-full h-full min-h-0 relative">
          {isYouTube && (
            <YouTubeHostStage
              isLive={currentWebinarSession?.status === 'IN_PROGRESS'}
              youtubeUrl={youtubeUrl}
            />
          )}
          {!isYouTube && !isEvergreen && (
            <LiveKitRoom
              roomName={moderatorToken?.roomName || ''}
              token={moderatorToken?.token || ''}
              isModerator
              userId={user?.id || ''}
              workspaceId={workspaceId}
              webinarId={webinarId}
              sessionId={sessionId}
              handleTriggerEvent={handleTriggerEvent}
              onVideoLayoutChange={onVideoLayoutChange}
              hasActiveTab={activePresenters || activeVideos || activeSlides}
            />
          )}
          {isEvergreen && (
            <>
              <video
                ref={evergreenVideoRef}
                src={evergreenVideoUrl}
                className="w-full h-full min-h-0 object-contain bg-black"
                playsInline
                controls={false}
                autoPlay
                muted={isEvergreenMuted}
              />
              {isEvergreenMuted && (
                <button
                  type="button"
                  onClick={handleActivateEvergreenSound}
                  className="absolute inset-0 z-10 flex h-full w-full min-h-0 flex-col flex-wrap items-center justify-center gap-6 cursor-pointer bg-black/70 p-2 hover:bg-black/80 backdrop-blur-sm md:p-8"
                >
                  <div
                    className="flex items-center justify-center p-3 rounded-xl"
                    style={{
                      backgroundColor: visualIdentity?.secondaryBackgroundColor
                        ? `${visualIdentity.secondaryBackgroundColor}20`
                        : '#ffffff',
                    }}
                  >
                    <Volume2
                      className="size-9"
                      style={{ color: visualIdentity?.secondaryBackgroundColor ?? '#ffffff' }}
                    />
                  </div>
                  <p className="text-white text-xl md:text-3xl font-bold wrap-break-word w-full">
                    {t('live.youtube.unmute.title')}
                  </p>
                  <p className="text-white text-xs md:text-sm font-normal wrap-break-word w-full">
                    {t('live.youtube.unmute.subtitle')}
                  </p>
                </button>
              )}
            </>
          )}
        </div>
        <div className="my-4 w-full">
          {!isEvergreen && (
            <div className="grid grid-cols-3 gap-4 w-full">
              {menuItems.map(({ id, title, icon: Icon }) => {
                const isActive = isItemActive(id);
                return (
                  <Button
                    size={'lg'}
                    key={id}
                    className={`p-4 h-full [&_svg:not([class*='size-'])]:size-6 relative rounded-lg flex flex-col gap-2 items-center justify-center cursor-pointer`}
                    onClick={() =>
                      handleItemActivation(
                        id as
                          | 'presenters'
                          | 'videos'
                          | 'slides'
                          | 'offer'
                          | 'quiz'
                          | 'survey'
                          | 'resources',
                      )
                    }
                    style={getConditionalBackgroundStyle({
                      theme: visualIdentity?.theme || 'LIGHT',
                      defaultIdentity: false,
                      defaultIdentityColor: visualIdentity?.secondaryBackgroundColor || '#C0D3FF',
                      isActive: isActive,
                      isModeratorLive: true,
                    })}
                  >
                    <div className="relative inline-flex">
                      <Icon
                        size={24}
                        color={getConditionalIconColor({
                          defaultIdentity: visualIdentity?.defaultIdentity,
                          secondaryBackgroundColor: isActive
                            ? visualIdentity?.backgroundColor
                            : visualIdentity?.secondaryBackgroundColor,
                        })}
                      />
                      <LiveResourceBadge count={getLiveCount(id)} />
                    </div>
                    <p
                      className="text-sm font-medium"
                      style={{
                        // Label lisible sur la surface active (qui suit le thème) : sombre en clair, blanc en sombre.
                        color: theme === 'DARK' ? '#FFFFFF' : '#000000',
                      }}
                    >
                      {title}
                    </p>
                  </Button>
                );
              })}
            </div>
          )}
        </div>
        {/* UPGRADE: liste d'attente pleine ou session terminée dans X minutes */}
        {isUpgrade && (
          <>
            <div className="bg-warning-light flex gap-2 rounded-lg w-full border-2 border-warning my-6 p-4">
              <Info size={24} color="#FFAF15" className="" />
              <div className="w-full flex flex-col gap-2">
                <p className="text-xl">
                  La salle de live est pleine! Il y a actuellement{' '}
                  <span className="font-bold">{'VARIABLE'}</span> personnes sur liste d'attente.
                </p>
                <div className="flex items-center gap-2 justify-between">
                  <p className="text-sm ">
                    Votre forfait ne vous permet d'accueillir que 500 participants. <br />
                    Augmentez votre capacité d'accueil en souscrivant à un forfait supérieur.
                  </p>
                  <Button>Souscrire au forfait Pro</Button>
                </div>
              </div>
            </div>

            <div className="bg-warning-light flex gap-2 rounded-lg w-full border-2 border-warning my-6 p-4">
              <Hourglass
                size={24}
                color="#FFAF15"
                className=" animate-spin animation-duration-5000"
              />
              <div className="w-full flex flex-col gap-2">
                <p className="text-xl">
                  Attention, la session sera automatiquement terminée dans{' '}
                  <span className="font-bold">{'VARIABLE'} minutes</span>
                </p>
                <div className="flex items-center gap-2 justify-between">
                  <p className="text-sm ">
                    Votre forfait ne vous permet de rester en Live que 2 heures. <br /> Augmentez
                    votre durée du live en souscrivant à un forfait supérieur.
                  </p>
                  <Button>Souscrire au forfait Pro</Button>
                </div>
              </div>
            </div>
          </>
        )}
        {/* footer */}
        <div>
          {channelName && (
            <FooterBar
              liveParticipantCount={liveParticipantCount}
              handleCloseSession={handleCloseSession}
              liveStartAt={currentWebinarSession?.startedAt ?? undefined}
              visualIdentity={visualIdentity}
              isModeratorLive={true}
              formattedPlannedDate={formattedPlannedDate}
              isActive={hasActiveItem}
              showSettings
              settings={liveSettings}
              onSettingsChange={onLiveSettingsChange}
              chatModeUpdating={chatModeUpdating}
              session={currentWebinarSession}
              isEvergreen={isEvergreen}
              evergreenElapsedDisplaySeconds={
                isEvergreen && currentWebinarSession?.startedAt ? evergreenFooterSeconds : undefined
              }
            />
          )}
        </div>
      </div>
      {/* panneau toujours monté, masqué quand aucun onglet n'est ouvert : le démonter couperait
          l'abonnement Ably des sondages et figerait leurs résultats */}
      <div
        className="w-1/3 h-full flex flex-col gap-4"
        style={{
          ...getConditionalBackgroundStyle({
            theme: visualIdentity?.theme || 'LIGHT',
            defaultIdentity: visualIdentity?.defaultIdentity,
            defaultIdentityColor: visualIdentity?.secondaryBackgroundColor || '#C0FFEA',
            isActive: true,
          }),
          color: theme === 'DARK' ? '#FFFFFF' : '#000000',
          ...(hasActiveItem ? {} : { display: 'none' }),
        }}
      >
        <div className="flex items-center justify-between p-4">
          <div className="text-xl font-medium">
            {menuItems.find((item) => isItemActive(item.id))?.title}
          </div>
          <X size={24} className="cursor-pointer" onClick={handleCloseActiveItem} />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* seule liste encore démontée : son rafraîchissement toutes les 12 s doit s'arrêter
              quand l'onglet est replié */}
          {activePresenters && (
            <PresentersList
              presenters={presenters}
              speakers={speakers}
              loading={presentersLoading}
              workspaceId={workspaceId}
              webinarId={webinarId}
              sessionId={sessionId}
              sessionIdentifier={currentWebinarSession.identifier}
              isModeratorLive={true}
            />
          )}
          {activeVideos && (
            <VideosList
              videos={videos}
              loading={webinarResourcesLoading}
              handleTriggerEvent={handleTriggerEvent}
              adminResourceStates={adminResourceStates}
              isModeratorLive={true}
            />
          )}
          {slidesMounted && (
            <div style={activeSlides ? undefined : { display: 'none' }}>
              <SlidesList
                slides={webinarSlides?.filter((s) => s.mimeType === 'application/pdf') ?? []}
                loading={webinarSlidesLoading}
                sessionId={sessionId}
                adminResourceStates={adminResourceStates}
                handleTriggerEvent={handleTriggerEvent}
                isModeratorLive={true}
              />
            </div>
          )}
          <div style={activeOffer ? undefined : { display: 'none' }}>
            <OfferList
              offers={offers}
              visualIdentity={visualIdentity}
              workspaceId={workspaceId}
              webinarId={webinarId}
              sessionId={sessionId}
              adminResourceStates={adminResourceStates}
              handleTriggerEvent={handleTriggerEvent}
              liveParticipantCountSync={liveParticipantCount}
              isModeratorLive
            />
          </div>
          <div style={activeQuiz ? undefined : { display: 'none' }}>
            <QuizsList
              quizs={quizs}
              visualIdentity={visualIdentity}
              workspaceId={workspaceId}
              webinarId={webinarId}
              adminResourceStates={adminResourceStates}
              handleTriggerEvent={handleTriggerEvent}
              liveParticipantCountSync={liveParticipantCount}
            />
          </div>
          {/* démonté au repliement : son chargement au montage sert de rafraîchissement,
              et il porte sa propre pagination */}
          {activeResources && (
            <SharedResourceLiveList
              workspaceId={workspaceId}
              webinarId={webinarId}
              sessionId={sessionId}
              sessionIdentifier={currentWebinarSession.identifier}
              visualIdentity={visualIdentity}
            />
          )}
          <div style={activeSurvey ? undefined : { display: 'none' }}>
            <SurveysList
              surveys={surveys}
              loading={surveyTemplatesLoading}
              visualIdentity={visualIdentity}
              workspaceId={workspaceId}
              webinarId={webinarId}
              sessionId={sessionId ?? undefined}
              adminResourceStates={adminResourceStates}
              handleTriggerEvent={handleTriggerEvent}
              liveParticipantCountSync={liveParticipantCount}
            />
          </div>
        </div>
      </div>
      <div className={`${hasActiveItem ? 'w-1/3' : 'w-1/2'} h-full`}>
        <div className="h-full flex flex-col min-h-0">
          <div className="w-full flex-1 min-h-0">
            <Tabs
              className=" rounded-none h-full flex flex-col min-h-0"
              value={activeTab}
              onValueChange={setActiveTab}
            >
              <TabsList
                className="w-full shrink-0 rounded-none"
                style={getConditionalBackgroundStyle({
                  theme: visualIdentity?.theme || 'LIGHT',
                  defaultIdentity: visualIdentity?.defaultIdentity,
                  defaultIdentityColor: visualIdentity?.secondaryBackgroundColor || '#C0FFEA',
                })}
              >
                <TabsTrigger
                  value="chat"
                  style={{
                    color: theme === 'DARK' ? '#FFFFFF' : '#000000',
                    borderBottomColor:
                      activeTab === 'chat'
                        ? visualIdentity?.defaultIdentity === true
                          ? '#6DFFCD'
                          : contrastColor
                        : secondaryBackgroundColor,
                  }}
                >
                  {t('adminLive.tabs.chat')}
                </TabsTrigger>
                <TabsTrigger
                  value="viewers"
                  style={{
                    color: theme === 'DARK' ? '#FFFFFF' : '#000000',
                    borderBottomColor:
                      activeTab === 'viewers'
                        ? visualIdentity?.defaultIdentity === true
                          ? '#6DFFCD'
                          : contrastColor
                        : secondaryBackgroundColor,
                  }}
                >
                  {t('adminLive.tabs.participants')}
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="chat"
                className="h-full flex flex-col min-h-0 data-[state=inactive]:hidden"
                forceMount={true}
              >
                <ChatView
                  workspaceId={workspaceId}
                  webinarId={webinarId}
                  sessionId={sessionId}
                  chatMode={liveSettings.chatMode}
                  channelName={channelName || ''}
                  userId={user?.id || ''}
                  userName={user?.firstName || ''}
                  isAdmin
                  liveParticipantCountSync={liveParticipantCount}
                  visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                />
              </TabsContent>
              <TabsContent value="viewers" className=" h-full flex flex-col min-h-0">
                {workspaceId && webinarId && sessionId && (
                  <AttendeesListView
                    workspaceId={workspaceId}
                    webinarId={webinarId}
                    sessionId={sessionId}
                    visualIdentity={visualIdentity as WorkspaceVisualIdentity}
                  />
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
